// ==============================================
// Super-admin "view as user" for the Navigation Library.
//
// Picking a customer here makes every Navigation Library request act as them
// — with FULL control, so shares, deletes and edits land in their data
// indistinguishably from something they did themselves. The banner is
// therefore not decoration: it is the only thing on screen telling a
// superadmin whose library they are about to change.
// ==============================================

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../services/api';

interface PickableUser {
  id: string;
  name?: string | null;
  email: string;
}

interface Props {
  /** The signed-in user's role — the picker renders for super_admin only. */
  role: string | undefined;
  /** Currently impersonated user, or null. */
  viewAs: PickableUser | null;
  onChange: (user: PickableUser | null) => void;
}

export function ViewAsPicker({ role, viewAs, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const boxRef = useRef<HTMLDivElement>(null);

  // Debounced so typing a name does not fire a query per keystroke.
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 250);
    return () => clearTimeout(t);
  }, [term]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const { data, isFetching } = useQuery({
    queryKey: ['viewAsUsers', debounced],
    queryFn: () => api.adminGetUsers({ search: debounced || undefined, limit: 20 }),
    enabled: role === 'super_admin' && open,
    staleTime: 30_000,
  });

  const users: PickableUser[] = useMemo(() => {
    const raw = (data as { users?: PickableUser[] } | PickableUser[] | undefined) ?? [];
    return Array.isArray(raw) ? raw : (raw.users ?? []);
  }, [data]);

  if (role !== 'super_admin') return null;

  const label = viewAs ? (viewAs.name || viewAs.email) : 'View as user';

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={[
          'flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
          viewAs
            ? 'border-amber-400 bg-amber-400/15 text-amber-200 hover:bg-amber-400/25'
            : 'border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700',
        ].join(' ')}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span aria-hidden>{viewAs ? '◉' : '○'}</span>
        <span className="max-w-[16rem] truncate">{label}</span>
        <span aria-hidden className="text-xs opacity-70">{'▾'}</span>
      </button>

      {open && (
        <div className="absolute left-0 z-50 mt-2 w-80 rounded-lg border border-slate-600 bg-slate-800 shadow-xl">
          <div className="border-b border-slate-700 p-2">
            <input
              autoFocus
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Search name or email..."
              className="w-full rounded-md border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:outline-none"
            />
          </div>

          <div className="max-h-72 overflow-y-auto py-1" role="listbox">
            {viewAs && (
              <button
                type="button"
                onClick={() => { onChange(null); setOpen(false); }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-sky-300 hover:bg-slate-700"
              >
                {'←'} Back to my own library
              </button>
            )}

            {isFetching && (
              <p className="px-3 py-3 text-sm text-slate-400">Searching...</p>
            )}

            {!isFetching && users.length === 0 && (
              <p className="px-3 py-3 text-sm text-slate-400">
                {debounced ? 'No users match that.' : 'Type to search users.'}
              </p>
            )}

            {users.map((u) => (
              <button
                key={u.id}
                type="button"
                role="option"
                aria-selected={viewAs?.id === u.id}
                onClick={() => { onChange(u); setOpen(false); setTerm(''); }}
                className="flex w-full flex-col items-start px-3 py-2 text-left hover:bg-slate-700"
              >
                <span className="text-sm font-medium text-slate-100">{u.name || 'Unnamed'}</span>
                <span className="text-sm text-slate-400">{u.email}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The banner. Deliberately loud.
 *
 * With full control there is nothing in the customer's own data to show that
 * an action came from support rather than from them, so the moment before the
 * action is the only place to make that obvious. A subtle chip in a corner is
 * exactly the thing someone stops seeing after ten minutes.
 */
export function ViewAsBanner({ viewAs, onExit }: { viewAs: PickableUser | null; onExit: () => void }) {
  if (!viewAs) return null;
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-400 bg-amber-400/15 px-4 py-3">
      <p className="text-sm text-amber-100">
        <strong className="font-semibold">Acting as {viewAs.name || viewAs.email}.</strong>{' '}
        Anything you change here — sharing, deleting, editing, sending to the boat — happens in
        their library, under their name.
      </p>
      <button
        type="button"
        onClick={onExit}
        className="shrink-0 rounded-md border border-amber-300 px-3 py-1.5 text-sm font-medium text-amber-100 hover:bg-amber-400/25"
      >
        Return to my library
      </button>
    </div>
  );
}
